import { createTheme, type MantineColorsTuple } from '@mantine/core';
import { colors, fontFamilies, radii, shadows } from './tokens';

/** Forge orange tuple — generated to span Mantine's 10-shade scale, anchored
 *  on `forgeStart` (#FF6B2C). */
const forgeOrange: MantineColorsTuple = [
  '#FFF1E8',
  '#FFD9C2',
  '#FFB994',
  '#FF9966',
  '#FF8044',
  '#FF6B2C',
  '#E55A1F',
  '#C24714',
  '#9E370B',
  '#7A2806',
];

const safety: MantineColorsTuple = [
  '#E6FFF3',
  '#B7FBD8',
  '#85F5BD',
  '#52EFA1',
  '#21E988',
  '#00E676',
  '#00C462',
  '#009E4F',
  '#00783C',
  '#005228',
];

const danger: MantineColorsTuple = [
  '#FFEBEF',
  '#FFC7D0',
  '#FF94A8',
  '#FF6082',
  '#FF335E',
  '#FF1744',
  '#DD0837',
  '#B8002C',
  '#8C0020',
  '#640014',
];

const warn: MantineColorsTuple = [
  '#FFF6E0',
  '#FFE3A6',
  '#FFD06D',
  '#FFC243',
  '#FFB826',
  '#FFB300',
  '#DD9700',
  '#B47900',
  '#885C00',
  '#5C3F00',
];

const ai: MantineColorsTuple = [
  '#F4ECFE',
  '#DEC9FB',
  '#C7A4F8',
  '#B284FC',
  '#A06DFB',
  '#BB86FC',
  '#9D63E5',
  '#7E48C0',
  '#5F359A',
  '#402274',
];

/** Neutral / surface scale — the dark palette we actually use on the page. */
const ink: MantineColorsTuple = [
  '#F0F0F5', // 0 — primary text
  '#D8D8E2',
  '#B8B8C8',
  '#9999B0',
  '#7A7A92',
  '#5C5C72',
  '#3F3F55',
  '#2A2A3A', // 7 — border
  '#1E1E2E', // 8 — elevated
  '#14141F', // 9 — surface
];

export const theme = createTheme({
  /* Brand colors */
  colors: {
    forge: forgeOrange,
    safety,
    warn,
    danger,
    ai,
    ink,
    /* Override Mantine's defaults so primaryColor variants pick our forge */
    orange: forgeOrange,
  },
  primaryColor: 'forge',
  primaryShade: { light: 5, dark: 5 },

  /* Type */
  fontFamily: fontFamilies.sans,
  fontFamilyMonospace: fontFamilies.mono,
  headings: {
    fontFamily: fontFamilies.sans,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '3rem', lineHeight: '1.1', fontWeight: '800' },
      h2: { fontSize: '2.25rem', lineHeight: '1.2', fontWeight: '700' },
      h3: { fontSize: '1.5rem', lineHeight: '1.3', fontWeight: '700' },
      h4: { fontSize: '1.25rem', lineHeight: '1.35', fontWeight: '600' },
      h5: { fontSize: '1.125rem', lineHeight: '1.4', fontWeight: '600' },
      h6: { fontSize: '1rem', lineHeight: '1.45', fontWeight: '600' },
    },
  },

  /* Layout */
  defaultRadius: 'md',
  radius: {
    xs: radii.sm,
    sm: radii.sm,
    md: radii.md,
    lg: radii.lg,
    xl: radii.xl,
  },

  /* Custom CSS variables — surfaced as var(--mantine-color-...) */
  other: {
    bg: colors.background,
    surface: colors.surface,
    elevated: colors.elevated,
    border: colors.border,
    forgeStart: colors.forgeStart,
    forgeEnd: colors.forgeEnd,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    cardShadow: shadows.card,
    elevatedShadow: shadows.elevated,
    forgeGlow: shadows.forgeGlow,
  },

  /* Components */
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          fontWeight: 600,
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
        withBorder: true,
      },
    },
    Anchor: {
      defaultProps: {
        underline: 'hover',
      },
    },
  },
});
